import React, { useState } from "react";
import {
  Card,
  Table,
  Row,
  Col,
  Form,
  InputGroup,
  Button,
  Badge,
  Alert,
  Tab,
  Tabs,
  Spinner
} from "react-bootstrap";
import {
  FileText,
  Heart,
  Activity,
  Calendar,
  Clock,
  CheckCircle,
  FileMedical,
  Syringe,
  Pill,
  Hospital,
  Microscope,
  Search,
  Download,
  Printer,
  InfoCircle,
  PersonCircle,
  Droplet,
  Phone,
  People,
  Prescription,
  GraphUp,
  Shield,
  CalendarEvent,
  ChevronRight,
  Award,
  Camera,
  ChevronDown,
  ChevronUp
} from "react-bootstrap-icons";
import { useAuth } from "../../contexts/AuthContext";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import cnamgsLogo from "../../assets/img/cnamgs_cover.jpeg";

// Données fictives du dossier médical (inchangées)
const mockMedicalRecord = {
  patientInfo: {
    name: "MOUKITA ROXANNE ESTELLE",
    id: "608 762 681 2",
    birthDate: "28/08/1976",
    bloodType: "A+",
    height: "165 cm",
    weight: "62 kg",
    allergies: ["Aucune allergie connue"],
    chronicDiseases: ["Aucune maladie chronique"],
    emergencyContact: {
      name: "Jean Moukita",
      phone: "+241 01 23 45 67",
      relationship: "Conjoint"
    },
    photo: "https://randomuser.me/api/portraits/women/68.jpg"
  },
  consultations: [
    { id: "C001", date: "2025-03-12", doctor: "Dr. Nzogho", specialty: "Médecine générale", reason: "Fièvre et maux de tête", diagnosis: "Grippe saisonnière", prescription: "Paracétamol, repos", status: "Terminé" },
    { id: "C002", date: "2025-03-01", doctor: "Dr. Mba", specialty: "Cardiologie", reason: "Contrôle annuel", diagnosis: "Normal", prescription: "Aucun traitement", status: "Terminé" },
    { id: "C003", date: "2025-02-20", doctor: "Dr. Ondo", specialty: "Dermatologie", reason: "Éruption cutanée", diagnosis: "Allergie légère", prescription: "Antihistaminique", status: "Terminé" },
    { id: "C004", date: "2025-02-10", doctor: "Dr. Nzogho", specialty: "Médecine générale", reason: "Toux persistante", diagnosis: "Bronchite", prescription: "Antibiotiques", status: "Terminé" },
    { id: "C005", date: "2025-01-25", doctor: "Dr. Mba", specialty: "Cardiologie", reason: "Palpitations", diagnosis: "Stress", prescription: "Relaxation", status: "Terminé" }
  ],
  prescriptions: [
    { id: "P001", date: "2025-03-12", doctor: "Dr. Nzogho", medicines: ["Paracétamol 500mg", "Vitamine C"], dosage: "2x/jour", duration: "5 jours", status: "En cours" },
    { id: "P002", date: "2025-03-01", doctor: "Dr. Mba", medicines: ["Aucun"], dosage: "-", duration: "-", status: "Terminé" },
    { id: "P003", date: "2025-02-20", doctor: "Dr. Ondo", medicines: ["Cetirizine 10mg"], dosage: "1x/jour", duration: "7 jours", status: "Terminé" },
    { id: "P004", date: "2025-02-10", doctor: "Dr. Nzogho", medicines: ["Amoxicilline 500mg"], dosage: "2x/jour", duration: "7 jours", status: "Terminé" }
  ],
  labResults: [
    { id: "L001", date: "2025-03-01", type: "Analyse sanguine", lab: "Laboratoire Central", results: "Normaux", status: "Disponible", file: "resultat_l001.pdf" },
    { id: "L002", date: "2025-02-15", type: "Glycémie", lab: "Laboratoire Central", results: "5.2 mmol/L (Normal)", status: "Disponible", file: "resultat_l002.pdf" },
    { id: "L003", date: "2025-01-20", type: "Cholestérol", lab: "Laboratoire Moderne", results: "1.8 g/L (Normal)", status: "Disponible", file: "resultat_l003.pdf" }
  ],
  vaccinations: [
    { id: "V001", date: "2025-02-15", vaccine: "Grippe saisonnière", doctor: "Dr. Nzogho", nextDose: "2026-02-15", status: "À jour" },
    { id: "V002", date: "2024-10-10", vaccine: "Tétanos", doctor: "Dr. Mba", nextDose: "2029-10-10", status: "À jour" },
    { id: "V003", date: "2024-05-20", vaccine: "Hépatite B", doctor: "Dr. Ondo", nextDose: "2025-05-20", status: "À renouveler" }
  ],
  upcomingAppointments: [
    { id: "A001", date: "2025-04-05", time: "10:30", doctor: "Dr. Nzogho", specialty: "Médecine générale", reason: "Consultation de suivi", status: "Confirmé" },
    { id: "A002", date: "2025-04-15", time: "14:00", doctor: "Dr. Mba", specialty: "Cardiologie", reason: "Contrôle annuel", status: "Confirmé" }
  ]
};

const MedicalRecord = () => {
  const { user } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('consultations');
  const [exporting, setExporting] = useState(false);
  const [printing, setPrinting] = useState(false);

  // États pour les lignes déroulantes (accordéon) sur mobile
  const [expandedConsultations, setExpandedConsultations] = useState(new Set());
  const [expandedPrescriptions, setExpandedPrescriptions] = useState(new Set());
  const [expandedLabResults, setExpandedLabResults] = useState(new Set());
  const [expandedVaccinations, setExpandedVaccinations] = useState(new Set());

  const toggleRow = (setter, id) => {
    setter(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) newSet.delete(id);
      else newSet.add(id);
      return newSet;
    });
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const getStatusBadge = (status) => {
    const badges = {
      "Terminé": <Badge bg="success" className="rounded-pill px-3 py-2"><CheckCircle size={12} className="me-1" /> Terminé</Badge>,
      "En cours": <Badge bg="warning" className="rounded-pill px-3 py-2"><Clock size={12} className="me-1" /> En cours</Badge>,
      "Disponible": <Badge bg="success" className="rounded-pill px-3 py-2"><CheckCircle size={12} className="me-1" /> Disponible</Badge>,
      "À jour": <Badge bg="success" className="rounded-pill px-3 py-2"><CheckCircle size={12} className="me-1" /> À jour</Badge>,
      "À renouveler": <Badge bg="danger" className="rounded-pill px-3 py-2"><Clock size={12} className="me-1" /> À renouveler</Badge>,
      "Confirmé": <Badge bg="info" className="rounded-pill px-3 py-2"><CheckCircle size={12} className="me-1" /> Confirmé</Badge>
    };
    return badges[status] || <Badge bg="secondary">{status}</Badge>;
  };

  const filteredConsultations = mockMedicalRecord.consultations.filter(c =>
    c.doctor.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.reason.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredPrescriptions = mockMedicalRecord.prescriptions.filter(p =>
    p.doctor.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.medicines.join().toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredLabResults = mockMedicalRecord.labResults.filter(l =>
    l.type.toLowerCase().includes(searchTerm.toLowerCase()) ||
    l.lab.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const statsCards = [
    {
      title: "Consultations",
      value: mockMedicalRecord.consultations.length,
      icon: People,
      color: "#4361ee",
      gradient: "linear-gradient(135deg, #4361ee 0%, #3b2a9f 100%)",
      bg: "white",
      trend: "+12%"
    },
    {
      title: "Ordonnances",
      value: mockMedicalRecord.prescriptions.length,
      icon: Prescription,
      color: "#10b981",
      gradient: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
      bg: "white",
      trend: "+8%"
    },
    {
      title: "Analyses",
      value: mockMedicalRecord.labResults.length,
      icon: GraphUp,
      color: "#06b6d4",
      gradient: "linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)",
      bg: "white",
      trend: "+5%"
    },
    {
      title: "Vaccinations",
      value: mockMedicalRecord.vaccinations.length,
      icon: Shield,
      color: "#f59e0b",
      gradient: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
      bg: "white",
      trend: "+3%"
    }
  ];

  // Génération PDF (inchangée)
  const generatePDF = async (action = 'save') => {
    setExporting(true);
    try {
      const doc = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      });

      const green = [0, 158, 96];
      const blue = [58, 117, 196];
      const white = [255, 255, 255];
      const grayLight = [248, 249, 250];
      const grayMedium = [108, 117, 125];
      const dark = [33, 37, 41];

      // Logo
      const logoX = 15, logoY = 10, logoW = 30, logoH = 20;
      doc.setFillColor(white[0], white[1], white[2]);
      doc.roundedRect(logoX, logoY, logoW, logoH, 3, 3, 'F');
      try {
        doc.addImage(cnamgsLogo, 'JPEG', logoX + 2, logoY + 1, logoW - 4, logoH - 2);
      } catch (e) {
        doc.setFontSize(12);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(blue[0], blue[1], blue[2]);
        doc.text("CNAMGS", logoX + 6, logoY + 12);
      }

      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(dark[0], dark[1], dark[2]);
      doc.text("CNAMGS - Assurance Maladie du Gabon", 50, 16);
      doc.text("B.P. 1234 - Libreville - Gabon", 50, 22);
      doc.text("Tél : +241 01 23 45 67 - contact@cnamgs.ga", 50, 28);

      doc.setFontSize(18);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(blue[0], blue[1], blue[2]);
      doc.text("DOSSIER MÉDICAL", 105, 42, { align: 'center' });

      doc.setDrawColor(blue[0], blue[1], blue[2]);
      doc.setLineWidth(0.3);
      doc.line(70, 46, 140, 46);

      doc.setFontSize(8);
      doc.setTextColor(grayMedium[0], grayMedium[1], grayMedium[2]);
      doc.text(`Généré le ${new Date().toLocaleDateString('fr-FR')} à ${new Date().toLocaleTimeString('fr-FR')}`, 190, 52, { align: 'right' });

      let y = 60;
      doc.setFillColor(grayLight[0], grayLight[1], grayLight[2]);
      doc.roundedRect(15, y, 180, 44, 2, 2, 'F');
      doc.setDrawColor(grayMedium[0], grayMedium[1], grayMedium[2]);
      doc.roundedRect(15, y, 180, 44, 2, 2, 'S');

      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(blue[0], blue[1], blue[2]);
      doc.text("Informations du patient", 20, y + 7);

      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(dark[0], dark[1], dark[2]);
      doc.text(`Nom complet : ${mockMedicalRecord.patientInfo.name}`, 20, y + 16);
      doc.text(`N° d'assuré : ${mockMedicalRecord.patientInfo.id}`, 20, y + 23);
      doc.text(`Date de naissance : ${mockMedicalRecord.patientInfo.birthDate}`, 20, y + 30);
      doc.text(`Groupe sanguin : ${mockMedicalRecord.patientInfo.bloodType}`, 20, y + 37);

      doc.text(`Taille / Poids : ${mockMedicalRecord.patientInfo.height} / ${mockMedicalRecord.patientInfo.weight}`, 100, y + 16);
      doc.text(`Allergies : ${mockMedicalRecord.patientInfo.allergies.join(", ")}`, 100, y + 23);
      doc.text(`Contact d'urgence : ${mockMedicalRecord.patientInfo.emergencyContact.name} (${mockMedicalRecord.patientInfo.emergencyContact.relationship})`, 100, y + 30);
      doc.text(`Téléphone : ${mockMedicalRecord.patientInfo.emergencyContact.phone}`, 100, y + 37);

      y += 52;
      doc.setFillColor(grayLight[0], grayLight[1], grayLight[2]);
      doc.roundedRect(15, y, 180, 28, 2, 2, 'F');
      doc.setDrawColor(grayMedium[0], grayMedium[1], grayMedium[2]);
      doc.roundedRect(15, y, 180, 28, 2, 2, 'S');

      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(blue[0], blue[1], blue[2]);
      doc.text("Synthèse des activités", 20, y + 6);

      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(dark[0], dark[1], dark[2]);

      const statsLines = [
        [`Consultations : ${mockMedicalRecord.consultations.length}`, `Ordonnances : ${mockMedicalRecord.prescriptions.length}`],
        [`Analyses : ${mockMedicalRecord.labResults.length}`, `Vaccinations : ${mockMedicalRecord.vaccinations.length}`]
      ];
      let lineY = y + 13;
      statsLines.forEach(line => {
        doc.text(line[0], 20, lineY);
        doc.text(line[1], 100, lineY);
        lineY += 7;
      });

      y += 36;
      const addTable = (title, head, body, columnStyles = null, startY) => {
        if (startY > 260) { doc.addPage(); startY = 20; }
        doc.setFontSize(12);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(blue[0], blue[1], blue[2]);
        doc.text(title, 15, startY);
        startY += 6;
        autoTable(doc, {
          startY: startY,
          head: [head],
          body: body,
          theme: 'grid',
          headStyles: { fillColor: green, textColor: white, fontSize: 8, fontStyle: 'bold', halign: 'center', valign: 'middle', cellPadding: 2.5 },
          bodyStyles: { fontSize: 7.5, valign: 'middle', cellPadding: 2 },
          margin: { left: 15, right: 15 },
          columnStyles: columnStyles,
          alternateRowStyles: { fillColor: grayLight },
          styles: { lineColor: [220, 220, 220], lineWidth: 0.1 }
        });
        return doc.lastAutoTable.finalY + 8;
      };

      y = addTable("Consultations",
        ['Date', 'Médecin', 'Spécialité', 'Motif', 'Diagnostic', 'Statut'],
        filteredConsultations.map(c => [formatDate(c.date), c.doctor, c.specialty, c.reason, c.diagnosis, c.status]),
        { 0: { cellWidth: 22 }, 1: { cellWidth: 27 }, 2: { cellWidth: 27 } },
        y
      );

      y = addTable("Ordonnances",
        ['Date', 'Médecin', 'Médicaments', 'Posologie', 'Durée', 'Statut'],
        filteredPrescriptions.map(p => [formatDate(p.date), p.doctor, p.medicines.join(", "), p.dosage, p.duration, p.status]),
        { 0: { cellWidth: 22 }, 1: { cellWidth: 27 }, 2: { cellWidth: 45 } },
        y
      );

      y = addTable("Analyses de laboratoire",
        ['Date', 'Type', 'Laboratoire', 'Résultats', 'Statut'],
        filteredLabResults.map(l => [formatDate(l.date), l.type, l.lab, l.results, l.status]),
        { 0: { cellWidth: 22 }, 1: { cellWidth: 30 }, 2: { cellWidth: 35 } },
        y
      );

      y = addTable("Vaccinations",
        ['Date', 'Vaccin', 'Médecin', 'Prochain rappel', 'Statut'],
        mockMedicalRecord.vaccinations.map(v => [formatDate(v.date), v.vaccine, v.doctor, formatDate(v.nextDose), v.status]),
        { 0: { cellWidth: 22 }, 1: { cellWidth: 35 }, 2: { cellWidth: 28 }, 3: { cellWidth: 28 } },
        y
      );

      y = addTable("Rendez-vous à venir",
        ['Date', 'Heure', 'Médecin', 'Spécialité', 'Motif', 'Statut'],
        mockMedicalRecord.upcomingAppointments.map(a => [formatDate(a.date), a.time, a.doctor, a.specialty, a.reason, a.status]),
        { 0: { cellWidth: 22 }, 1: { cellWidth: 18 }, 2: { cellWidth: 27 }, 3: { cellWidth: 27 } },
        y
      );

      if (y + 35 > 280) { doc.addPage(); y = 20; }
      doc.setDrawColor(grayMedium[0], grayMedium[1], grayMedium[2]);
      doc.line(20, y, 190, y);
      y += 8;
      doc.setFontSize(9);
      doc.setFont('helvetica', 'bold');
      doc.text("Pour la CNAMGS", 30, y);
      doc.text("Pour le patient", 140, y);
      y += 15;
      doc.setFont('helvetica', 'normal');
      doc.text("(Signature et cachet)", 30, y);
      doc.text("(Signature)", 140, y);
      y += 12;
      doc.setFontSize(8);
      doc.setTextColor(grayMedium[0], grayMedium[1], grayMedium[2]);
      doc.text("Document confidentiel – Tous droits réservés", 105, y + 8, { align: 'center' });

      const pageCount = doc.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setDrawColor(grayMedium[0], grayMedium[1], grayMedium[2]);
        doc.line(15, 285, 195, 285);
        doc.setFontSize(7);
        doc.setTextColor(grayMedium[0], grayMedium[1], grayMedium[2]);
        doc.text(`CNAMGS Gabon – Assurance Maladie – Page ${i} / ${pageCount}`, 105, 292, { align: 'center' });
      }

      if (action === 'save') {
        doc.save(`Dossier_medical_${mockMedicalRecord.patientInfo.name.replace(/\s/g, '_')}.pdf`);
      } else if (action === 'print') {
        const blob = doc.output('blob');
        const url = URL.createObjectURL(blob);
        const printWindow = window.open(url);
        printWindow?.print();
        URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('Erreur lors de la génération du PDF:', error);
      alert('Erreur lors de la génération du PDF');
    } finally {
      setExporting(false);
      setPrinting(false);
    }
  };

  const handleExport = () => generatePDF('save');
  const handlePrint = () => generatePDF('print');

  return (
    <div className="" style={{ fontFamily: "'Inter', sans-serif" }}>
      <div className="container-fluid">
        {/* En-tête (inchangé) */}
        <div className="mb-4">
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
            <div>
              <ol className="breadcrumb fs-sm mb-1">
                <li className="breadcrumb-item"><a href="/patient">Tableau de bord</a></li>
                <li className="breadcrumb-item active">Dossier médical</li>
              </ol>
              <h4 className="main-title mb-0 d-flex align-items-center gap-2">
                <div className="bg-primary bg-opacity-10 rounded-circle p-2">
                  <FileMedical size={24} className="text-primary" />
                </div>
                Mon dossier médical
              </h4>
              <p className="text-muted mb-0 mt-1">
                Consultez l'ensemble de vos informations médicales
              </p>
            </div>
            <div className="d-flex gap-2">
              <Button variant="outline-primary" size="sm" className="rounded-pill px-3" onClick={handleExport} disabled={exporting}>
                {exporting ? <Spinner animation="border" size="sm" className="me-1" /> : <Download size={14} className="me-1" />}
                {exporting ? "Export..." : "Exporter"}
              </Button>
              <Button variant="outline-secondary" size="sm" className="rounded-pill px-3" onClick={handlePrint} disabled={printing}>
                {printing ? <Spinner animation="border" size="sm" className="me-1" /> : <Printer size={14} className="me-1" />}
                {printing ? "Préparation..." : "Imprimer"}
              </Button>
            </div>
          </div>
        </div>

        {/* Informations personnelles (inchangées) */}
        <Row className="g-4 mb-4">
          <Col md={12}>
            <Card className="border-0 shadow-sm bg-white" style={{ borderRadius: '20px', overflow: 'hidden' }}>
              <div className="p-4" style={{ background: "linear-gradient(135deg, #667eea 0%, #122c9f 100%)" }}>
                <h5 className="mb-0 fw-semibold d-flex align-items-center gap-2 text-white">
                  <PersonCircle size={20} />
                  Informations personnelles
                </h5>
              </div>
              <Card.Body className="p-4">
                <Row className="g-4">
                  <Col md={3} className="text-center">
                    <div className="position-relative d-inline-block">
                      <div style={{
                        width: '140px',
                        height: '140px',
                        borderRadius: '50%',
                        overflow: 'hidden',
                        border: `4px solid #fff`,
                        boxShadow: '0 8px 20px rgba(0,0,0,0.15)',
                        margin: '0 auto',
                        background: '#f0f0f0'
                      }}>
                        <img
                          src={mockMedicalRecord.patientInfo.photo}
                          alt="Photo de profil"
                          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        />
                      </div>
                      <div className="position-absolute bottom-0 end-0 bg-primary rounded-circle p-2" style={{ transform: 'translate(10%, 10%)' }}>
                        <Camera size={16} className="text-white" />
                      </div>
                    </div>
                    <div className="mt-3">
                      <Badge bg="success" className="rounded-pill px-3 py-2">
                        <CheckCircle size={12} className="me-1" /> Vérifié
                      </Badge>
                    </div>
                    <div className="mt-3">
                      <div className="p-3 rounded-3" style={{ background: "linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)", borderLeft: "4px solid #f59e0b" }}>
                        <small className="text-muted d-block mb-1">Groupe sanguin</small>
                        <strong className="fs-6"><Droplet size={14} className="text-danger me-1" /> {mockMedicalRecord.patientInfo.bloodType}</strong>
                      </div>
                    </div>
                  </Col>
                  <Col md={9}>
                    <Row className="g-3">
                      <Col md={4} xs={6}>
                        <div className="p-3 rounded-3" style={{ background: "linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)", borderLeft: "4px solid #4361ee" }}>
                          <small className="text-muted d-block mb-1">Nom complet</small>
                          <strong style={{ fontSize: '12px' }}>{mockMedicalRecord.patientInfo.name}</strong>
                        </div>
                      </Col>
                      <Col md={4} xs={6}>
                        <div className="p-3 rounded-3" style={{ background: "linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)", borderLeft: "4px solid #10b981" }}>
                          <small className="text-muted d-block mb-1">Numéro d'assuré</small>
                          <strong className="fs-6">{mockMedicalRecord.patientInfo.id}</strong>
                        </div>
                      </Col>
                      <Col md={4} xs={6}>
                        <div className="p-3 rounded-3" style={{ background: "linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)", borderLeft: "4px solid #06b6d4" }}>
                          <small className="text-muted d-block mb-1">Date de naissance</small>
                          <strong className="fs-6">{mockMedicalRecord.patientInfo.birthDate}</strong>
                        </div>
                      </Col>
                      <Col md={4} xs={6}>
                        <div className="p-3 rounded-3" style={{ background: "linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)", borderLeft: "4px solid #ef4444" }}>
                          <small className="text-muted d-block mb-1">Taille / Poids</small>
                          <strong className="fs-6">{mockMedicalRecord.patientInfo.height} / {mockMedicalRecord.patientInfo.weight}</strong>
                        </div>
                      </Col>
                      <Col md={8}>
                        <div className="p-3 rounded-3" style={{ background: "linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)" }}>
                          <small className="text-muted d-block mb-1">Allergies</small>
                          <Badge bg="warning" className="rounded-pill px-3 py-2">{mockMedicalRecord.patientInfo.allergies.join(", ")}</Badge>
                        </div>
                      </Col>
                    </Row>
                    <hr className="my-3" />
                    <div className="d-flex align-items-center gap-4 flex-wrap p-3 rounded-3" style={{ background: "#f8f9fa" }}>
                      <div>
                        <small className="text-muted d-block mb-1">Contact d'urgence</small>
                        <strong>{mockMedicalRecord.patientInfo.emergencyContact.name}</strong>
                      </div>
                      <div>
                        <Phone size={14} className="text-muted me-1" />
                        <span>{mockMedicalRecord.patientInfo.emergencyContact.phone}</span>
                      </div>
                      <div>
                        <span className="text-muted">{mockMedicalRecord.patientInfo.emergencyContact.relationship}</span>
                      </div>
                    </div>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Cartes de statistiques */}
        <Row className="g-3 mb-4">
          {statsCards.map((card, idx) => (
            <Col md={3} xs={6} key={idx}>
              <Card className="border-0 shadow-sm overflow-hidden stats-card" style={{ borderRadius: '16px', background: card.bg }}>
                <div className="stats-card-gradient" style={{ background: card.gradient }}></div>
                <Card.Body className="p-3">
                  <div className="d-flex justify-content-between align-items-start">
                    <div>
                      <h6 className="text-muted mb-1 small">{card.title}</h6>
                      <h2 className="mb-0 fw-bold display-6">{card.value}</h2>
                      <div className="mt-2">
                        <Badge className="rounded-pill px-2 py-1" style={{ background: card.color, color: "white", fontSize: "10px" }}>
                          <GraphUp size={10} className="me-1" />
                          {card.trend}
                        </Badge>
                      </div>
                    </div>
                    <div className="stats-icon" style={{ background: card.gradient }}>
                      <card.icon size={28} className="text-white" />
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>

        {/* Barre de recherche */}
        <Row className="mb-4">
          <Col md={12}>
            <Card className="border-0 shadow-sm bg-white" style={{ borderRadius: '16px' }}>
              <Card.Body>
                <InputGroup>
                  <InputGroup.Text className="bg-light border-0">
                    <Search size={16} className="text-muted" />
                  </InputGroup.Text>
                  <Form.Control
                    type="text"
                    placeholder="Rechercher dans votre dossier médical..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="border-0 bg-light"
                  />
                </InputGroup>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Onglets avec tableaux adaptés mobile */}
        <Row>
          <Col md={12}>
            <Card className="border-0 shadow-sm bg-white" style={{ borderRadius: '16px', overflow: 'hidden' }}>
              <Tabs
                activeKey={activeTab}
                onSelect={(k) => setActiveTab(k)}
                className="border-bottom px-3 pt-2"
                fill
              >
                {/* Consultations */}
                <Tab eventKey="consultations" title={<span><People className="me-2" /> Consultations</span>}>
                  <div className="p-3">
                    <div className="table-responsive">
                      <Table hover className="mb-0">
                        <thead className="bg-light">
                          <tr>
                            <th>Date</th>
                            <th>Médecin</th>
                            <th className="d-none d-sm-table-cell">Spécialité</th>
                            <th className="d-none d-sm-table-cell">Motif</th>
                            <th className="d-none d-sm-table-cell">Diagnostic</th>
                            <th className="d-none d-sm-table-cell">Statut</th>
                            <th className="d-sm-none">Détails</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredConsultations.map(c => {
                            const isExpanded = expandedConsultations.has(c.id);
                            return (
                              <React.Fragment key={c.id}>
                                <tr className="align-middle">
                                  <td data-label="Date" className="small">{formatDate(c.date)}</td>
                                  <td data-label="Médecin" className="fw-medium">{c.doctor}</td>
                                  <td data-label="Spécialité" className="d-none d-sm-table-cell">{c.specialty}</td>
                                  <td data-label="Motif" className="d-none d-sm-table-cell">{c.reason}</td>
                                  <td data-label="Diagnostic" className="d-none d-sm-table-cell">{c.diagnosis}</td>
                                  <td data-label="Statut" className="d-none d-sm-table-cell">{getStatusBadge(c.status)}</td>
                                  <td className="d-sm-none text-center">
                                    <Button variant="link" onClick={() => toggleRow(setExpandedConsultations, c.id)} className="p-0 text-secondary" style={{ textDecoration: 'none' }}>
                                      {isExpanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                                    </Button>
                                  </td>
                                </tr>
                                {isExpanded && (
                                  <tr className="d-sm-none detail-row">
                                    <td colSpan="7" className="p-3 bg-light">
                                      <div className="row g-2">
                                        <div className="col-6"><strong>Spécialité :</strong> {c.specialty}</div>
                                        <div className="col-6"><strong>Motif :</strong> {c.reason}</div>
                                        <div className="col-6"><strong>Diagnostic :</strong> {c.diagnosis}</div>
                                        <div className="col-6"><strong>Statut :</strong> {getStatusBadge(c.status)}</div>
                                      </div>
                                    </td>
                                  </tr>
                                )}
                              </React.Fragment>
                            );
                          })}
                        </tbody>
                      </Table>
                    </div>
                  </div>
                </Tab>

                {/* Prescriptions */}
                <Tab eventKey="prescriptions" title={<span><Prescription className="me-2" /> Ordonnances</span>}>
                  <div className="p-3">
                    <div className="table-responsive">
                      <Table hover className="mb-0">
                        <thead className="bg-light">
                          <tr>
                            <th>Date</th>
                            <th>Médecin</th>
                            <th className="d-none d-sm-table-cell">Médicaments</th>
                            <th className="d-none d-sm-table-cell">Posologie</th>
                            <th className="d-none d-sm-table-cell">Durée</th>
                            <th className="d-none d-sm-table-cell">Statut</th>
                            <th className="d-sm-none">Détails</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredPrescriptions.map(p => {
                            const isExpanded = expandedPrescriptions.has(p.id);
                            return (
                              <React.Fragment key={p.id}>
                                <tr className="align-middle">
                                  <td data-label="Date" className="small">{formatDate(p.date)}</td>
                                  <td data-label="Médecin" className="fw-medium">{p.doctor}</td>
                                  <td data-label="Médicaments" className="d-none d-sm-table-cell">{p.medicines.join(", ")}</td>
                                  <td data-label="Posologie" className="d-none d-sm-table-cell">{p.dosage}</td>
                                  <td data-label="Durée" className="d-none d-sm-table-cell">{p.duration}</td>
                                  <td data-label="Statut" className="d-none d-sm-table-cell">{getStatusBadge(p.status)}</td>
                                  <td className="d-sm-none text-center">
                                    <Button variant="link" onClick={() => toggleRow(setExpandedPrescriptions, p.id)} className="p-0 text-secondary" style={{ textDecoration: 'none' }}>
                                      {isExpanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                                    </Button>
                                  </td>
                                </tr>
                                {isExpanded && (
                                  <tr className="d-sm-none detail-row">
                                    <td colSpan="7" className="p-3 bg-light">
                                      <div className="row g-2">
                                        <div className="col-6"><strong>Médicaments :</strong> {p.medicines.join(", ")}</div>
                                        <div className="col-6"><strong>Posologie :</strong> {p.dosage}</div>
                                        <div className="col-6"><strong>Durée :</strong> {p.duration}</div>
                                        <div className="col-6"><strong>Statut :</strong> {getStatusBadge(p.status)}</div>
                                      </div>
                                    </td>
                                  </tr>
                                )}
                              </React.Fragment>
                            );
                          })}
                        </tbody>
                      </Table>
                    </div>
                  </div>
                </Tab>

                {/* Analyses */}
                <Tab eventKey="lab-results" title={<span><GraphUp className="me-2" /> Analyses</span>}>
                  <div className="p-3">
                    <div className="table-responsive">
                      <Table hover className="mb-0">
                        <thead className="bg-light">
                          <tr>
                            <th>Date</th>
                            <th className="d-none d-sm-table-cell">Type</th>
                            <th className="d-none d-sm-table-cell">Laboratoire</th>
                            <th className="d-none d-sm-table-cell">Résultats</th>
                            <th className="d-none d-sm-table-cell">Statut</th>
                            <th className="d-sm-none">Détails</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredLabResults.map(l => {
                            const isExpanded = expandedLabResults.has(l.id);
                            return (
                              <React.Fragment key={l.id}>
                                <tr className="align-middle">
                                  <td data-label="Date" className="small">{formatDate(l.date)}</td>
                                  <td data-label="Type" className="d-none d-sm-table-cell">{l.type}</td>
                                  <td data-label="Laboratoire" className="d-none d-sm-table-cell">{l.lab}</td>
                                  <td data-label="Résultats" className="d-none d-sm-table-cell">{l.results}</td>
                                  <td data-label="Statut" className="d-none d-sm-table-cell">{getStatusBadge(l.status)}</td>
                                  <td className="d-sm-none text-center">
                                    <Button variant="link" onClick={() => toggleRow(setExpandedLabResults, l.id)} className="p-0 text-secondary" style={{ textDecoration: 'none' }}>
                                      {isExpanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                                    </Button>
                                  </td>
                                </tr>
                                {isExpanded && (
                                  <tr className="d-sm-none detail-row">
                                    <td colSpan="6" className="p-3 bg-light">
                                      <div className="row g-2">
                                        <div className="col-6"><strong>Type :</strong> {l.type}</div>
                                        <div className="col-6"><strong>Laboratoire :</strong> {l.lab}</div>
                                        <div className="col-6"><strong>Résultats :</strong> {l.results}</div>
                                        <div className="col-6"><strong>Statut :</strong> {getStatusBadge(l.status)}</div>
                                        <div className="col-12 mt-2">
                                          <Button variant="link" size="sm" className="p-0 text-primary">
                                            <FileText size={16} className="me-1" /> Voir le document
                                          </Button>
                                        </div>
                                      </div>
                                    </td>
                                  </tr>
                                )}
                              </React.Fragment>
                            );
                          })}
                        </tbody>
                      </Table>
                    </div>
                  </div>
                </Tab>

                {/* Vaccinations */}
                <Tab eventKey="vaccinations" title={<span><Shield className="me-2" /> Vaccinations</span>}>
                  <div className="p-3">
                    <div className="table-responsive">
                      <Table hover className="mb-0">
                        <thead className="bg-light">
                          <tr>
                            <th>Date</th>
                            <th className="d-none d-sm-table-cell">Vaccin</th>
                            <th className="d-none d-sm-table-cell">Médecin</th>
                            <th className="d-none d-sm-table-cell">Prochain rappel</th>
                            <th className="d-none d-sm-table-cell">Statut</th>
                            <th className="d-sm-none">Détails</th>
                          </tr>
                        </thead>
                        <tbody>
                          {mockMedicalRecord.vaccinations.map(v => {
                            const isExpanded = expandedVaccinations.has(v.id);
                            return (
                              <React.Fragment key={v.id}>
                                <tr className="align-middle">
                                  <td data-label="Date" className="small">{formatDate(v.date)}</td>
                                  <td data-label="Vaccin" className="d-none d-sm-table-cell">{v.vaccine}</td>
                                  <td data-label="Médecin" className="d-none d-sm-table-cell">{v.doctor}</td>
                                  <td data-label="Prochain rappel" className="d-none d-sm-table-cell">{formatDate(v.nextDose)}</td>
                                  <td data-label="Statut" className="d-none d-sm-table-cell">{getStatusBadge(v.status)}</td>
                                  <td className="d-sm-none text-center">
                                    <Button variant="link" onClick={() => toggleRow(setExpandedVaccinations, v.id)} className="p-0 text-secondary" style={{ textDecoration: 'none' }}>
                                      {isExpanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                                    </Button>
                                  </td>
                                </tr>
                                {isExpanded && (
                                  <tr className="d-sm-none detail-row">
                                    <td colSpan="6" className="p-3 bg-light">
                                      <div className="row g-2">
                                        <div className="col-6"><strong>Vaccin :</strong> {v.vaccine}</div>
                                        <div className="col-6"><strong>Médecin :</strong> {v.doctor}</div>
                                        <div className="col-6"><strong>Prochain rappel :</strong> {formatDate(v.nextDose)}</div>
                                        <div className="col-6"><strong>Statut :</strong> {getStatusBadge(v.status)}</div>
                                      </div>
                                    </td>
                                  </tr>
                                )}
                              </React.Fragment>
                            );
                          })}
                        </tbody>
                      </Table>
                    </div>
                  </div>
                </Tab>

                {/* Rendez-vous (déjà en cartes, pas de modification) */}
                <Tab eventKey="appointments" title={<span><CalendarEvent className="me-2" /> Rendez-vous</span>}>
                  <div className="p-3">
                    <Row className="g-3">
                      {mockMedicalRecord.upcomingAppointments.map(a => (
                        <Col md={6} key={a.id}>
                          <Card className="border-0 shadow-sm h-100" style={{ borderRadius: '16px' }}>
                            <Card.Body>
                              <div className="d-flex justify-content-between align-items-start mb-2">
                                <h6 className="fw-bold mb-0">{a.doctor}</h6>
                                {getStatusBadge(a.status)}
                              </div>
                              <p className="small text-muted mb-2">{a.specialty}</p>
                              <div className="d-flex gap-3 small mb-2">
                                <span><Calendar size={12} className="me-1" /> {formatDate(a.date)}</span>
                                <span><Clock size={12} className="me-1" /> {a.time}</span>
                              </div>
                              <p className="small mb-3">{a.reason}</p>
                              <Button variant="outline-primary" size="sm" className="rounded-pill w-100">
                                Modifier
                              </Button>
                            </Card.Body>
                          </Card>
                        </Col>
                      ))}
                    </Row>
                  </div>
                </Tab>
              </Tabs>
            </Card>
          </Col>
        </Row>

        {/* Information */}
        <Row className="mt-4">
          <Col md={12}>
            <Alert variant="info" className="border-0 rounded-3 shadow-sm" style={{ background: "linear-gradient(135deg, #e8f4fd 0%, #d9eaff 100%)" }}>
              <div className="d-flex align-items-center gap-3">
                <InfoCircle size={24} className="text-info" />
                <div>
                  <strong className="d-block mb-1">Accès à votre dossier médical</strong>
                  <span className="small">Votre dossier médical est confidentiel et protégé. Seuls vous et vos médecins traitants y avez accès.</span>
                </div>
              </div>
            </Alert>
          </Col>
        </Row>

        <style>{`
          .nav-tabs .nav-link {
            border: none;
            color: #6c757d;
            padding: 1rem 1rem;
            font-weight: 500;
            transition: all 0.2s;
          }
          .nav-tabs .nav-link:hover {
            border: none;
            color: #0d6efd;
            background: transparent;
          }
          .nav-tabs .nav-link.active {
            color: #0d6efd;
            border-bottom: 3px solid #0d6efd;
            background: transparent;
          }
          .table > :not(caption) > * > * {
            padding: 0.75rem 0.75rem;
            vertical-align: middle;
          }
          .stats-card {
            position: relative;
            transition: transform 0.3s ease, box-shadow 0.3s ease;
            border-radius: 16px;
            overflow: hidden;
          }
          .stats-card:hover {
            transform: translateY(-5px);
            box-shadow: 0 12px 24px rgba(0, 0, 0, 0.12) !important;
          }
          .stats-card-gradient {
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 4px;
            opacity: 0.8;
          }
          .stats-icon {
            width: 48px;
            height: 48px;
            border-radius: 12px;
            display: flex;
            align-items: center;
            justify-content: center;
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
          }
          .display-6 {
            font-size: 1.75rem;
            font-weight: 600;
          }
          @media (max-width: 768px) {
            .stats-icon {
              width: 40px;
              height: 40px;
            }
            .stats-icon svg {
              width: 20px;
              height: 20px;
            }
            .display-6 {
              font-size: 1.5rem;
            }
            .table td, .table th {
              padding: 0.5rem;
            }
            .btn-sm {
              padding: 0.25rem 0.5rem;
              font-size: 0.75rem;
            }
            .detail-row td {
              padding: 0.75rem;
            }
          }
        `}</style>
      </div>
    </div>
  );
};

export default MedicalRecord;